import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  CameraMovementFeedbackKind,
  MovementFeedbackPulse,
} from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.types.ts';

import { MOVEMENT_FEEDBACK_VISIBLE_MS } from './cameraMovementFeedback.constants.ts';

type ActiveMovementFeedbackPulses = Partial<
  Record<CameraMovementFeedbackKind, MovementFeedbackPulse>
>;

/**
 * Holds active overlay pulses for {@link CameraMovementFeedbackOverlay}.
 * Each movement kind has its own pulse and hide timer so simultaneous inputs
 * (e.g. pan-left + tilt-up) can flash at their edge placements independently.
 */
export const useCameraMovementFeedback = () => {
  const [pulses, setPulses] = useState<ActiveMovementFeedbackPulses>({});
  const pulseIdRef = useRef(0);
  const hideTimersRef = useRef<
    Partial<Record<CameraMovementFeedbackKind, ReturnType<typeof setTimeout>>>
  >({});

  const trigger = useCallback((kind: CameraMovementFeedbackKind) => {
    const id = ++pulseIdRef.current;
    setPulses(current => ({ ...current, [kind]: { kind, id } }));
    const existingTimer = hideTimersRef.current[kind];
    if (existingTimer) clearTimeout(existingTimer);
    hideTimersRef.current[kind] = setTimeout(() => {
      setPulses(current => {
        const pulse = current[kind];
        if (pulse?.id !== id) return current;
        // oxlint-disable-next-line typescript/no-unused-vars
        const { [kind]: _removed, ...rest } = current;
        return rest;
      });
      delete hideTimersRef.current[kind];
    }, MOVEMENT_FEEDBACK_VISIBLE_MS);
  }, []);

  useEffect(
    () => () => {
      for (const timer of Object.values(hideTimersRef.current)) {
        if (timer) clearTimeout(timer);
      }
    },
    []
  );

  return { pulses, trigger };
};
